import { type ReactNode } from "react";
import { cn } from "@/lib/utils";

const tones = {
  neutral: "bg-slate-100 text-slate-700 ring-slate-200",
  ok: "bg-green-50 text-green-800 ring-green-200",
  warning: "bg-amber-50 text-amber-900 ring-amber-200",
  blocking: "bg-red-50 text-red-800 ring-red-200",
  info: "bg-blue-50 text-blue-800 ring-blue-200",
  brand: "bg-brand-50 text-brand-800 ring-brand-100",
} as const;

export type BadgeTone = keyof typeof tones;

export function Badge({
  tone = "neutral",
  children,
  className,
}: {
  tone?: BadgeTone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/** The status of an isolate, coloured consistently across every screen. */
export function StatusBadge({ status }: { status: string }) {
  const tone: BadgeTone =
    status === "complete"
      ? "ok"
      : status === "void"
        ? "neutral"
        : status === "needs_review"
          ? "warning"
          : "info";

  const label = status.replace(/_/g, " ");
  return <Badge tone={tone}>{label}</Badge>;
}
